import { useMemo } from 'react';
import { Item } from './item-types';
import { colors } from '../shared/ui-extension';
import { Stack, Typography } from '@mui/material';

type StatusIndicator = {
  title: string | ((item: Item) => string);
  /// Return `true` for C&P orange, `false` for an outline,
  // or a custom color code.
  color: (item: Item) => string | boolean;
};

const statusIndicators: StatusIndicator[] = [
  {
    title: 'Web Active',
    color: (item) => item.ecc_enabled_flag,
  },
  {
    title: 'Needs Packing (Warehouse)',
    color: (item) => item.needs_packing_flag,
  },
  {
    title: 'Lid Included',
    color: (item) => item.is_lid_included,
  },
  {
    title: 'Drop-Ship Only',
    color: (item) => item.is_ds_only,
  },
  {
    title: (item) => {
      const salesMultiple = item.uoms?.find((u) => u.unit_id === 'CA-SM');
      const salesMultipleText = salesMultiple ? ` (${salesMultiple.unit_size})` : '';
      return `Full Case Only${salesMultipleText}`
    },
    color: (item) => {
      const salesMultiple = item.uoms?.find((u) => u.unit_id === 'CA-SM');
      return salesMultiple !== undefined;
    },
  },
  {
    title: 'Call to Order',
    color: (item) => item.is_call_to_order,
  },
  {
    title: 'No Samples',
    color: (item) => item.no_sample_flag,
  },
  {
    title: 'SO / Web Promo',
    color: (item) => item.so_web_promo_flag,
  },
];

export default function ItemStatuses({ item }: { item: Item }) {
  const statuses = useMemo(() => {
    return statusIndicators
      .map((si) => {
        return {
          ...si,
          active: si.color(item),
        };
      })
      .sort((a, b) => {
        if (!a.active && b.active) return 1;
        if (a.active && !b.active) return -1;
        return 0;
      })
      .map((si) => {
        let title = typeof si.title === 'string' ? si.title : si.title(item);
        let active = si.color(item);
        let indicatorSize = 14;
        let color = '';
        let outline = '';
        let textColor = 'black';
        if (typeof active == 'string') {
          color = active;
        } else if (active) {
          color = colors.highlightBlue;
        } else if (!active) {
          outline = `2px solid ${colors.darkGray}`;
          textColor = colors.darkGray;
        }
        return {
          title,
          color,
          indicatorSize,
          outline,
          textColor,
        };
      });
  }, [item.item_id]);
  return (
    <div
      style={{ gridTemplateColumns: '1fr 1fr', width: '100%', display: 'grid' }}
    >
      {statuses.map((si) => (
        <Stack direction={'row'} alignItems={'center'} gap={1} useFlexGap>
          <div
            style={{
              width: si.indicatorSize,
              height: si.indicatorSize,
              background: si.color,
              borderRadius: si.indicatorSize / 2,
              border: si.outline,
            }}
          ></div>
          <Typography sx={{ color: si.textColor }}>{si.title}</Typography>
        </Stack>
      ))}
    </div>
  );
}
